import { useSuspenseQuery } from '@tanstack/react-query';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import type { ContentReferenceContext } from '@gitbook/app-database';
import {
    BLOCK_REUSABLE_CONTENT,
    type BlockReusableContentData,
    createEditorValue,
    removeBlock,
} from '@gitbook/doc-core';
import type { HiveWorkingBranch } from '@gitbook/hive-client';
import {
    CONTENT_DEFAULT_BRANCH,
    getReusableContentTitle,
    type RevisionReusableContent,
} from '@gitbook/hive-core';
import {
    HiveWorkingDocumentProvider,
    useHiveBranch,
    useHiveBranchById,
    useHiveClient,
    useHiveComputedDocument,
    useHiveRevision,
    useHiveWorkingBranch,
} from '@gitbook/hive-react';
import { BlockRange, type Document, TextRange } from '@gitbook/slate';
import type { BlockProps, EditorContainer } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import {
    Badge,
    Button,
    ButtonGroup,
    Description,
    DialogRoot,
    EmptyState,
    EmptyStateActions,
    EmptyStateIcon,
    Heading,
    type IconDefinition,
    useDialogTriggerState,
} from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';
import { getSuspense } from '@gitbook/util-suspense-cache';
import {
    assertIsDefined,
    filterOutNullable,
    hasMandateProp,
    type MandateProps,
} from '@gitbook/util-ts';

import { useNavigation } from '../../../navigation';
import { apiQuery } from '../../../state';
import { APIErrorBoundary } from '../../APIErrorBoundary';
import {
    ContentReferenceContextProvider,
    useContentReferenceContext,
} from '../../ContentReferences';
import { RenameReusableContentDialog } from '../../ReusableContent';
import { BlockLoadingFallback, BlockWrapper, useBlockPaletteFactory } from '../common';
import { useIsBlockHovered } from '../common/layout/hovered';
import { ContentEditorContext } from '../contexts';
import { ReferenceBlockActions, ReferenceBlockFrame } from '../ReferenceBlockFrame';
import { ReusableContentEditor, ReusableContentEditorWithValue } from './ReusableContentEditor';

/**
 * Component to render a reusable content block.
 */
export function BlockReusableContent(props: BlockProps) {
    const { node, editor } = props;
    const data = node.data.toFrozenObject<BlockReusableContentData>();
    const { readOnly } = editor;

    const handleRemove = React.useMemo(() => {
        if (readOnly) {
            return;
        }

        return () =>
            editor.change((change) => {
                removeBlock(change, node.key).focus();
            });
    }, [readOnly]);

    if (data.ref.space) {
        return (
            <React.Suspense
                fallback={
                    <BlockWrapper {...props}>
                        <BlockLoadingFallback />
                    </BlockWrapper>
                }
            >
                <OtherSpaceReusableContent
                    {...props}
                    data={data}
                    space={data.ref.space}
                    handleRemove={handleRemove}
                />
            </React.Suspense>
        );
    }

    return <SameSpaceReusableContent {...props} data={data} handleRemove={handleRemove} />;
}

/**
 * Render reusable content from the same space, using the current branch/revision as context.
 */
function SameSpaceReusableContent(
    props: BlockProps & {
        data: BlockReusableContentData;
        handleRemove: (() => void) | undefined;
    }
) {
    const { data, node, editor, handleRemove } = props;
    const reusableContentId = data.ref.reusableContent;
    const branch = useHiveBranch();
    const { revision } = useHiveRevision();
    const { logAnalyticsEvent } = React.useContext(ContentEditorContext);

    const reusableContent = revision.reusableContents[reusableContentId];
    const reusableContentEditorRef = React.useRef<EditorContainer>(null);

    const detachReusableContent = React.useCallback(() => {
        if (editor.readOnly || !branch) {
            return;
        }

        const reusableContentEditor = reusableContentEditorRef.current;
        assertIsDefined(reusableContentEditor);

        editor.change((change) => {
            change.select(BlockRange.createAtBlock(node));
            change.insertDocument(reusableContentEditor.value.document);
        });

        logAnalyticsEvent({
            type: 'edit_reusable_content_detach',
        });
    }, [editor, branch, node, logAnalyticsEvent]);

    if (!reusableContent) {
        // Reusable Content isn't on this Branch (or it's been deleted)
        return (
            <BlockWrapper {...props}>
                <ReusableContentErrorFallback
                    onRemove={handleRemove}
                    heading="Reusable content not found"
                    description="The reusable content may have been removed from the space."
                />
            </BlockWrapper>
        );
    }

    // We consider the block read-only if the editor is read-only or the block is not selected.
    // In terms of UX, the user will first have to click on the block to select it
    // before being able to interact with it.
    // It ensures only one reusable content block is editable at a time, solving the issue of
    // multiple editors being open at the same time in the page.
    const readOnly = editor.readOnly || !props.isSelected;

    return (
        <BlockReusableContentWrapper
            {...props}
            detachReusableContent={detachReusableContent}
            reusableContent={reusableContent}
        >
            <BlockReusableContentInner
                ref={reusableContentEditorRef}
                reusableContent={reusableContent}
                branch={branch}
                contentId={revision.contentId}
                readOnly={readOnly}
            />
        </BlockReusableContentWrapper>
    );
}

/**
 * Render reusable content from another space.
 */
function OtherSpaceReusableContent(
    props: BlockProps & {
        space: string;
        data: BlockReusableContentData;
        handleRemove: (() => void) | undefined;
    }
) {
    const { space: spaceId, data, handleRemove } = props;

    // Fetch the space from the API. We'll need it for the title, icon etc. but we'll
    // also do this to check whether we have access.
    const { data: space, error } = useSuspenseQuery(
        apiQuery('getSpaceById', { spaceId: spaceId }, { nullOnError: true })
    );

    if (!space || !space.permissions.access) {
        const { heading, description } = (() => {
            if (error?.statusCode === 403 || (space && !space.permissions.access)) {
                return {
                    heading: 'No access to this space',
                    description:
                        "You don't have access to the space containing this reusable content.",
                };
            }

            if (error?.statusCode === 404) {
                return {
                    heading: 'Space not found',
                    description:
                        "The space containing this reusable content doesn't exist anymore.",
                };
            }

            return {
                heading: "Couldn't load reusable content",
                description: 'This reusable content could not be loaded.',
            };
        })();

        // Space not found or no access.
        return (
            <BlockWrapper {...props}>
                <ReusableContentErrorFallback
                    onRemove={handleRemove}
                    heading={heading}
                    description={description}
                />
            </BlockWrapper>
        );
    }

    return <ReusableContentWithOtherSpace {...props} space={space} data={data} />;
}

function ReusableContentContentRefProvider(props: {
    reusableContent: string;
    space: string;
    children: React.ReactNode;
}) {
    const { reusableContent, space, children } = props;
    const context = useContentReferenceContext();

    const contextWithReusable = React.useMemo<ContentReferenceContext>(() => {
        return {
            ...context,
            reusableContent: {
                id: reusableContent,
                space: space,
            },
        };
    }, [context, reusableContent, space]);

    return (
        <ContentReferenceContextProvider value={contextWithReusable}>
            {children}
        </ContentReferenceContextProvider>
    );
}

function ReusableContentWithOtherSpace(
    props: BlockProps & {
        space: api.Space;
        data: BlockReusableContentData;
        handleRemove: (() => void) | undefined;
    }
) {
    const { space, data, handleRemove } = props;
    const branch = useHiveBranchById({
        contentId: space.id,
        branchId: CONTENT_DEFAULT_BRANCH,
        presence: undefined,
        readOnly: true,
    });
    const { revision } = useHiveWorkingBranch(branch);
    const reusableContentId = data.ref.reusableContent;
    const reusableContent = revision.reusableContents[reusableContentId];
    const { navigate } = useNavigation();

    if (!reusableContent) {
        // Reusable Content isn't on this Branch (or it's been deleted)
        return (
            <BlockWrapper {...props}>
                <ReusableContentErrorFallback
                    onRemove={handleRemove}
                    heading="Reusable content not found"
                    description="The reusable content may have been removed from its parent space."
                />
            </BlockWrapper>
        );
    }

    return (
        <ReusableContentContentRefProvider reusableContent={reusableContentId} space={space.id}>
            <BlockReusableContentWrapper
                readOnly
                {...props}
                reusableContent={reusableContent}
                externalLink={{
                    label: `Shared from ${space.title}`,
                    icon: Icon.ArrowUpRight,
                    onPress: () => {
                        navigate({
                            screen: {
                                type: 'space',
                                space: space.id,
                            },
                        });
                    },
                }}
            >
                <BlockReusableContentInner
                    reusableContent={reusableContent}
                    branch={branch}
                    contentId={space.id}
                    readOnly
                />
            </BlockReusableContentWrapper>
        </ReusableContentContentRefProvider>
    );
}

function BlockReusableContentWrapper(
    props: BlockProps & {
        data: BlockReusableContentData;
        reusableContent: RevisionReusableContent;
        externalLink?: { icon: IconDefinition; label: string; onPress: () => void };
        readOnly?: boolean;
        detachReusableContent?: () => void;
    }
) {
    const { node, editor, reusableContent, detachReusableContent, children } = props;
    const readOnly = props.readOnly ?? editor.readOnly;

    const renameDialogTrigger = useDialogTriggerState();
    const { logAnalyticsEvent } = React.useContext(ContentEditorContext);

    const palette = useBlockPaletteFactory({
        nodeKey: node.key,
        createExtraGroups: () =>
            [
                {
                    key: 'reusable-content-main-actions',
                    commands: [
                        {
                            key: 'rename',
                            title: 'Rename',
                            icon: Icon.Type,
                            trigger: () => {
                                renameDialogTrigger.open();
                            },
                        },
                    ],
                },
                detachReusableContent
                    ? {
                          key: 'reusable-content-other-actions',
                          commands: [
                              {
                                  key: 'detach',
                                  title: 'Detach',
                                  icon: Icon.AIReplaceSelection,
                                  trigger: () => {
                                      detachReusableContent();
                                  },
                              },
                          ],
                      }
                    : null,
            ].filter(filterOutNullable),
    });

    const handleRemove = useEventCallback(() => {
        editor.change((change) => {
            removeBlock(change, node.key).focus();
        });
    });

    return (
        <>
            <BlockWrapper {...props} palette={readOnly ? undefined : palette}>
                <APIErrorBoundary
                    fallback={{
                        404: ({ resetError }) => (
                            <ReusableContentErrorFallback
                                onRemove={readOnly ? undefined : handleRemove}
                                onRetry={resetError}
                            />
                        ),
                    }}
                >
                    <React.Suspense fallback={<BlockLoadingFallback />}>
                        <ContentFrame
                            blockKey={node.key}
                            document={props.editor.value.document}
                            readOnly={readOnly}
                            isFocused={props.isFocused || props.isSelected}
                            externalLink={props.externalLink}
                            onChangeTitle={() => {
                                if (readOnly) {
                                    return;
                                }

                                renameDialogTrigger.open();
                            }}
                            title={getReusableContentTitle(reusableContent.title)}
                            onMouseOver={() => {
                                if (readOnly || props.isSelected) {
                                    return;
                                }
                                // Two reusable content blocks can point to the same reusable content.
                                // This means that they will share a datasync.
                                // If one is deselected at the same time as the other is selected,
                                // the dataSync readOnly state can be incorrect. To prevent this,
                                // if the currently selected value includes a reusable content block
                                // that points to the same reusable content we deselect it first.
                                const { value } = editor;
                                if (
                                    value.blocks.size > 0 &&
                                    TextRange.isTextRange(value.selection) &&
                                    value.blocks.some(
                                        (b) =>
                                            b.type === BLOCK_REUSABLE_CONTENT &&
                                            b.data.get('ref').reusableContent === reusableContent.id
                                    )
                                ) {
                                    props.editor.change((change) => change.deselect());
                                    return;
                                }
                            }}
                            onMouseDown={() => {
                                if (readOnly || props.isSelected) {
                                    return;
                                }

                                props.editor.change((change) => {
                                    const textNode = node.getFirstText();
                                    assertIsDefined(textNode);
                                    // Set selection to the start of the void reusable content block.
                                    // We won't focus the editor here as it can trigger a scroll-to-selection
                                    // when we want to be able to focus inside the reusable content editor document.
                                    change.select(
                                        TextRange.create({
                                            anchorKey: textNode.key,
                                            focusKey: textNode.key,
                                            anchorOffset: 0,
                                            focusOffset: 0,
                                        })
                                    );
                                });
                            }}
                        >
                            {children}
                        </ContentFrame>
                    </React.Suspense>
                </APIErrorBoundary>
            </BlockWrapper>
            <DialogRoot {...renameDialogTrigger.rootProps}>
                <RenameReusableContentDialog
                    reusableContent={reusableContent}
                    onRenamed={() => {
                        logAnalyticsEvent({
                            type: 'reusable_content_rename',
                            method: 'block',
                        });
                    }}
                />
            </DialogRoot>
        </>
    );
}

const BlockReusableContentInner = React.forwardRef(function BlockReusableContentInner(
    props: {
        reusableContent: RevisionReusableContent;
        contentId: string;
        branch?: HiveWorkingBranch | null;
        readOnly: boolean;
    },
    ref: React.ForwardedRef<EditorContainer>
) {
    const { reusableContent, branch, contentId, readOnly } = props;
    const editorContext = React.useContext(ContentEditorContext);

    if (hasMandateProp(reusableContent, 'computed')) {
        return (
            <ReusableContentComputedContent
                ref={ref}
                contentId={contentId}
                reusableContent={reusableContent}
            />
        );
    }

    if (branch) {
        return (
            <HiveWorkingDocumentProvider
                branch={branch}
                containerType="reusable-contents"
                containerId={reusableContent.id}
                readOnly={readOnly}
            >
                <ReusableContentEditor
                    ref={ref}
                    readOnly={readOnly}
                    copyPaste={editorContext.copyPaste}
                />
            </HiveWorkingDocumentProvider>
        );
    }

    return (
        <ReusableContentFrozenContent
            ref={ref}
            contentId={contentId}
            reusableContent={reusableContent}
        />
    );
});

/**
 * Fetch the document for a reusable content block and display a static version of it.
 * This is used when rendering a frozen content, not from the datasync.
 */
const ReusableContentFrozenContent = React.forwardRef(function ReusableContentFrozenContent(
    props: {
        contentId: string;
        reusableContent: RevisionReusableContent;
    },
    ref: React.ForwardedRef<EditorContainer>
) {
    const { contentId, reusableContent } = props;
    const editorContext = React.useContext(ContentEditorContext);

    const hiveClient = useHiveClient();
    const document = reusableContent.document
        ? getSuspense(hiveClient.store.getDocument(contentId, reusableContent.document))
        : undefined;

    const value = React.useMemo(() => {
        return createEditorValue(document);
    }, [document]);

    return (
        <ReusableContentEditorWithValue
            ref={ref}
            value={value}
            onChange={() => {}}
            readOnly
            copyPaste={editorContext.copyPaste}
            withCompletions={false}
        />
    );
});

/**
 * Fetch the document for a reusable content block when it's a computed content;
 * and display a static version of it.
 */
const ReusableContentComputedContent = React.forwardRef(function ReusableContentComputedContent(
    props: {
        contentId: string;
        reusableContent: MandateProps<RevisionReusableContent, 'computed'>;
    },
    ref: React.ForwardedRef<EditorContainer>
) {
    const { contentId, reusableContent } = props;
    const editorContext = React.useContext(ContentEditorContext);

    const document = useHiveComputedDocument({
        contentId,
        computedSeed: reusableContent.computed.seed,
        computedSource: reusableContent.computed.source,
    });

    const value = React.useMemo(() => {
        return createEditorValue(document);
    }, [document]);

    return (
        <ReusableContentEditorWithValue
            ref={ref}
            value={value}
            onChange={() => {}}
            readOnly
            copyPaste={editorContext.copyPaste}
            withCompletions={false}
        />
    );
});

/**
 * Fallback component to render when reusable content fails to load.
 */
function ReusableContentErrorFallback(props: {
    heading?: React.ReactNode;
    description?: React.ReactNode;
    onRemove?: () => void;
    onRetry?: () => void;
}) {
    const {
        onRemove,
        onRetry,
        heading = 'Error while loading reusable content',
        description = 'This reusable content could not be loaded.',
    } = props;

    return (
        <div className="rounded border">
            <EmptyState>
                <EmptyStateIcon>
                    <Icon.Alert />
                </EmptyStateIcon>
                <Heading>{heading}</Heading>
                <Description>{description}</Description>
                {onRemove || onRetry ? (
                    <EmptyStateActions>
                        {onRemove ? (
                            <Button
                                kind="secondary"
                                label="Remove block"
                                onPress={() => onRemove()}
                            />
                        ) : null}
                        {onRetry ? <Button label="Retry" onPress={() => onRetry()} /> : null}
                    </EmptyStateActions>
                ) : null}
            </EmptyState>
        </div>
    );
}

function ContentFrame(props: {
    isFocused: boolean;
    blockKey: string;
    readOnly: boolean;
    document: Document;
    children: React.ReactNode;
    onChangeTitle: () => void;
    title: string;
    externalLink?: { icon: IconDefinition; label: string; onPress: () => void };
    onMouseDown: () => void;
    onMouseOver: () => void;
}) {
    const isBlockHovered = useIsBlockHovered(props.blockKey);

    return (
        // biome-ignore lint/a11y/useKeyWithMouseEvents: keyboard interaction is handled at the editor level
        // biome-ignore lint/a11y/noStaticElementInteractions: keyboard interaction is handled at the editor level
        <div onMouseDown={props.onMouseDown} onMouseOver={props.onMouseOver} className="py-2">
            <ReferenceBlockFrame isFocused={props.isFocused} isHovered={isBlockHovered}>
                <ContentActions
                    readOnly={props.readOnly}
                    onChangeTitle={props.onChangeTitle}
                    title={props.title}
                    externalLink={props.externalLink}
                />
            </ReferenceBlockFrame>
            {props.children}
        </div>
    );
}

const ContentActions = React.memo(function BlockFrameButtonActions(props: {
    title: string;
    onChangeTitle: () => void;
    externalLink?: { icon: IconDefinition; label: string; onPress: () => void };
    readOnly: boolean;
}) {
    return (
        <ReferenceBlockActions>
            {props.readOnly ? (
                <Badge
                    icon={Icon.ReusableContent}
                    label={props.title}
                    size="small"
                    className="shrink select-none"
                />
            ) : (
                <ButtonGroup kind="segmented">
                    <Button
                        onPress={props.onChangeTitle}
                        tooltip={{ label: 'Rename reusable content' }}
                        size="small"
                        label={props.title}
                        leadingIcon={Icon.ReusableContent}
                        className="shrink"
                    />

                    {props.externalLink ? (
                        <Button
                            size="small"
                            label={props.externalLink.label}
                            onPress={props.externalLink.onPress}
                            leadingIcon={props.externalLink.icon}
                            iconOnly
                        />
                    ) : null}
                </ButtonGroup>
            )}
        </ReferenceBlockActions>
    );
});
